//@ts-nocheck
import { useEffect, useMemo, useRef, useState } from "react";
import { ContainerOutlined, FormOutlined } from "@ant-design/icons";
import { Menu, Dropdown, message, Modal, Button as AntdButton } from "antd";
import { useNavigate } from "react-router-dom";
import { safeCallAsync } from "@xemplo/safe-call";
import {
  findAuthorizationClaims,
  getCurrentAuthorizationDom,
  isComponentVisibleEnabled,
} from "../../../libs";
import {
  PAYRUN_AUTHORIZATION_CLAIMS,
  PAYRUN_MENU_ITEM,
  PAYRUN_STATUS_IDS,
  OUTSOURCE_MODEL_ID,
} from "../../../constants";
import { useAppSelector } from "../../../hooks";
import PayrunChangeLogModal from "./ChangeLogModal";
import {
  DawnArrowLongRight,
  DawnDownload,
  DawnMenuMore,
  DawnPencilEdit,
  DawnInvoiceTick,
  DawnSync,
  DawnPeopleProfile,
} from "@xemplo/icons";
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  ButtonType,
} from "@xemplo/button";
import { useModal } from "@xemplo/modal";
import { EditPayrunMilestones } from "@xemplo/edit-payrun-milestones";
import { UpdatePayrunStatus, createStatusPayload } from "./UpdatePayrunStatus";
import { AuthorizationDOM, PersonaRole } from "@xemplo/gp-types";
import {
  getPayrun,
  updatePayrunDates,
  updateReimportEarninglines,
  generatePayrunInvoice,
  exportABAFile,
} from "../../actions/payrun";
import { useHttpClient } from "@xemplo/http";
import { useRoleAuthorisation } from "@xemplo/roles-query";

import { useDispatch } from "react-redux";
import { SkipPayrunPrompt } from "./SkipPayrunPrompt/SkipPayrunPrompt";
import { usePromptDialog } from "@xemplo/prompt-dialog";
import {
  AssignPayrollManagerModal,
  ModalDialogRef,
} from "@xemplo/assign-payroll-manager";
import { useSkipPayruns, useGetPayrunByIdQuery } from "@xemplo/payrun-query";

const PayrunViewOptions = (props: any) => {
  const { isInRole } = useRoleAuthorisation();
  const isSa = isInRole([PersonaRole.SystemAdmin]);
  const member = useAppSelector((state) => state.member);
  const navigate = useNavigate();
  const { showAuditReport, payrun } = props;

  const openEditMilestones = useRef<(() => void) | null>(null);
  const { data: milestonePayrun, dataUpdatedAt: milestoneUpdatedAt } =
    useGetPayrunByIdQuery({ id: payrun?.id });

  const prevMilestoneUpdatedAt = useRef<number | null>(null);
  const dispatchAction = useDispatch();
  const { cancelSignal } = useHttpClient();

  /** Update pay run status (legacy milestones flow, status-only) */
  const [viewStatusModal, setViewStatusModal] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  //Assign Payroll Manager Modal
  const assignPayrunManagerModalRef = useRef<ModalDialogRef>(null);
  function handleAssignPayrollManager() {
    assignPayrunManagerModalRef.current?.openModalDialog();
  }

  const handleMenuClick = (value: any) => {
    switch (value?.key) {
      case "detail":
        return navigate(`/payrun/${payrun?.id}/detail`);
      case "auditReport":
        return navigate(`/payrun/${payrun?.id}/details/auditReport`);
      case "changeLog":
        return handleChangeLogClick();
      case "editMilestones":
        return openEditMilestones.current?.();
      case "updateStatus":
        return setViewStatusModal(true);
      case "skipPayrun":
        return handleSkipButton();
      case "reimport":
        return dispatchAction(updateReimportEarninglines(true));
      case "regenerate-invoice":
        return openRegenerateInvoiceModal();
      case "reimport-aba":
        return openReimportABAModal();
      case "assign-payroll-manager":
        return handleAssignPayrollManager();
      default:
        break;
    }
  };

  const claims = getCurrentAuthorizationDom(member);
  const payrunClaims = findAuthorizationClaims({
    claims,
    name: PAYRUN_MENU_ITEM,
  });

  /** Change Log Modal Stuff  */
  const [changeLogModalVisible, setChangeLogModalVisible] = useState(false);
  const handleChangeLogClick = () => {
    setChangeLogModalVisible(true);
  };
  const handleChangeLogModalClose = () => {
    setChangeLogModalVisible(false);
  };

  const { configureModal, toggleModal } = useModal();

  useEffect(() => {
    return () => {
      toggleModal(false);
    };
  }, []);

  /** Trigger refetch of payrun data after successful milestone update */
  useEffect(() => {
    if (!milestoneUpdatedAt) return;
    if (prevMilestoneUpdatedAt.current === null) {
      prevMilestoneUpdatedAt.current = milestoneUpdatedAt;
      return;
    }
    if (milestoneUpdatedAt !== prevMilestoneUpdatedAt.current) {
      prevMilestoneUpdatedAt.current = milestoneUpdatedAt;
      dispatchAction(getPayrun({ id: payrun.id, options: {} }));
    }
  }, [milestoneUpdatedAt, dispatchAction, payrun.id]);

  const handleStatusModalClose = () => {
    setViewStatusModal(false);
    setSelectedStatusId(null);
  };

  const handleSaveStatus = () => {
    setStatusLoading(true);
    const payload = createStatusPayload(payrun, selectedStatusId);
    updatePayrunDates({
      payload,
      cancelToken: cancelSignal.token,
    }).then((resp) => {
      setStatusLoading(false);
      if (resp?.status === 200) {
        handleStatusModalClose();
        dispatchAction(
          getPayrun({
            id: payrun.id,
            options: {},
            cancelToken: cancelSignal.token,
          }),
        );
        message.success("Pay Run status updated successfully");
      }
    });
  };

  /** Skip Payrun */
  const { mutateAsync: skipPayruns } = useSkipPayruns();
  const skipPrompt = usePromptDialog({
    id: "skip-payrun=prompt",
  });

  const handleSkipPayrun = async () => {
    const [result, error] = await safeCallAsync(
      skipPayruns({
        payrunIds: [payrun.id],
      }),
    );

    if (error) {
      skipPrompt.closeDialog();
      message.error("Error skipping Pay Run, please try again");
      return;
    }

    if (result) {
      message.success("Pay Run successfully skipped");
      skipPrompt.closeDialog();
      navigate("/payrun");
    }
  };

  const handleSkipButton = () => {
    skipPrompt.openModalDialog();
  };

  const regeneratePayrunInvoice = () => {
    toggleModal(false);

    generatePayrunInvoice({ id: payrun.id }).then((resp) => {
      if (resp.status) {
        message.success("Invoice generated successfully");
      } else {
        message.error(resp.data?.validationErrors?.errors?.[0]?.description);
      }
    });
  };

  const reimportABA = () => {
    toggleModal(false);
    exportABAFile({ id: payrun.id });
  };

  const openRegenerateInvoiceModal = () => {
    configureModal({
      open: true,
      header: {
        title: "Regenerate Invoice",
      },
      body: {
        content: <>Are you sure you want to regenerate the invoice?</>,
      },
      footer: {
        btnPrimary: {
          label: "Generate",
          type: ButtonType.Primary,
          onClick: regeneratePayrunInvoice,
          ariaLabel: "Regenerate invoice button",
        },
        btnTertiary: {
          label: "Cancel",
          type: ButtonType.Tertiary,
          onClick: () => {
            toggleModal(false);
          },
          ariaLabel: "Regenerate cancel button",
        },
      },
    });
  };

  const openReimportABAModal = () => {
    configureModal({
      open: true,
      header: {
        title: "Reimport ABA file",
      },
      body: {
        content: <>Are you sure you want to reimport the ABA file?</>,
      },
      footer: {
        btnPrimary: {
          label: "Import",
          type: ButtonType.Primary,
          onClick: reimportABA,
          ariaLabel: "Import button",
        },
        btnTertiary: {
          label: "Cancel",
          type: ButtonType.Tertiary,
          onClick: () => {
            toggleModal(false);
          },
          ariaLabel: "Import cancel button",
        },
      },
    });
  };

  const canAccessPayrunRegeneratePdfButton =
    [
      PAYRUN_STATUS_IDS.INVOICE_GENERATED_SYSTEM_TRIGGERED,
      PAYRUN_STATUS_IDS.INVOICE_GENERATED_USER_TRIGGERED,
    ].includes(payrun?.payrunStatusId) &&
    payrunClaims?.components?.find(
      (o: AuthorizationDOM) =>
        o.name === PAYRUN_AUTHORIZATION_CLAIMS.payrunRegeneratePdfButton,
    );

  const payrunSkipClaim = payrunClaims?.components?.find(
    (o: AuthorizationDOM) =>
      o.name === PAYRUN_AUTHORIZATION_CLAIMS.payrunSkipButton,
  );

  const payrunEditClaim = payrunClaims?.components?.find(
    (o: AuthorizationDOM) =>
      o.name === PAYRUN_AUTHORIZATION_CLAIMS.payrunEditButton,
  );
  const userCanEdit =
    isSa ||
    (isComponentVisibleEnabled({ claim: payrunEditClaim }) &&
      ![PAYRUN_STATUS_IDS.COMPLETED, PAYRUN_STATUS_IDS.PROCESSED].includes(
        payrun.payrunStatusId,
      ));

  //User can skip if payrun is in Pending Amendment, Pending Preparation, Pending Approval
  const userCanSkip =
    isComponentVisibleEnabled({ claim: payrunSkipClaim }) &&
    PAYRUN_STATUS_IDS.COMPLETED !== payrun.payrunStatusId;

  // Status change is only valid for the same statuses the status select renders for.
  const canUpdateStatus =
    userCanEdit &&
    [
      PAYRUN_STATUS_IDS.AMENDEMENTS_SUBMITTED_SYSTEM_TRIGGERED,
      PAYRUN_STATUS_IDS.AMENDEMENTS_SUBMITTED_USER_TRIGGERED,
      PAYRUN_STATUS_IDS.PREPARED_USER_TRIGGERED,
    ].includes(payrun.payrunStatusId);

  const canReimportEarnings = useMemo(() => {
    if (
      payrun.payrunStatusId <=
      PAYRUN_STATUS_IDS.CONFIRMED_BY_PAYROLL_ADMIN_USER_TRIGGERED
    )
      return false;
    const payrunReimportClaim = payrunClaims?.components?.find(
      (o: AuthorizationDOM) =>
        o.name === PAYRUN_AUTHORIZATION_CLAIMS.payrunReimportButton,
    );
    return isComponentVisibleEnabled({ claim: payrunReimportClaim });
  }, [payrun.payrunStatusId, payrunClaims?.components]);

  const canAccessImportABAButton =
    payrun?.payrollModelId === OUTSOURCE_MODEL_ID &&
    [PAYRUN_STATUS_IDS.COMPLETED].includes(payrun?.payrunStatusId) &&
    payrunClaims?.components?.find(
      (o: AuthorizationDOM) =>
        o.name === PAYRUN_AUTHORIZATION_CLAIMS.payrunReimportAbaButton,
    );

  /** Drop down Menu options */
  const menu = (
    <Menu onClick={handleMenuClick} className="user-authentication">
      <Menu.Item key="detail">
        <ContainerOutlined /> Pay Run Detail
      </Menu.Item>
      {(payrun?.auditReportGenerated || showAuditReport) && (
        <Menu.Item key="auditReport">
          <FormOutlined /> Audit Report
        </Menu.Item>
      )}
      <Menu.Item key="changeLog">
        <ContainerOutlined /> Change Log
      </Menu.Item>
      {userCanEdit && (
        <Menu.Item key="editMilestones">
          <div className="flex ">
            <DawnPencilEdit fontSize={24} className="mt-1 mr-3" /> Edit
          </div>
        </Menu.Item>
      )}
      {canUpdateStatus && (
        <Menu.Item key="updateStatus">
          <div className="flex">
            <DawnSync fontSize={24} className="mt-1 mr-3" /> Update Status
          </div>
        </Menu.Item>
      )}
      {userCanSkip && (
        <Menu.Item key="skipPayrun">
          <div className="flex">
            <DawnArrowLongRight fontSize={24} className="mt-1 mr-3" /> Skip
          </div>
        </Menu.Item>
      )}
      {canReimportEarnings && (
        <Menu.Item key="reimport">
          <div className="flex">
            <DawnDownload fontSize={24} className="mt-1 mr-3" /> Reimport
            Earnings
          </div>
        </Menu.Item>
      )}
      {canAccessPayrunRegeneratePdfButton && (
        <Menu.Item key="regenerate-invoice">
          <div className="flex">
            <DawnInvoiceTick className="mt-1 mr-3" /> Regenerate Invoice file
          </div>
        </Menu.Item>
      )}
      {canAccessImportABAButton && (
        <Menu.Item key="reimport-aba">
          <div className="flex">
            <DawnSync fontSize={24} className="mt-1 mr-3" /> Reimport ABA file
          </div>
        </Menu.Item>
      )}
      <Menu.Item key="assign-payroll-manager">
        <div className="flex">
          <DawnPeopleProfile fontSize={24} className="mt-1 mr-3" /> Assign
          Payroll Manager
        </div>
      </Menu.Item>
    </Menu>
  );
  return (
    <>
      <Dropdown trigger={["click"]} overlay={menu}>
        <Button
          type={ButtonType.Secondary}
          className="ml-2"
          size={ButtonSize.Small}
          ariaLabel="options-button"
          icon={<DawnMenuMore fontSize={24} />}
          iconPosition={ButtonIconPosition.Trailing}
        >
          Options
        </Button>
      </Dropdown>
      {/** TODO: Convert this modal to @xemplo/modal  */}
      <PayrunChangeLogModal
        visible={changeLogModalVisible}
        handleClose={handleChangeLogModalClose}
        title={payrun?.name}
        payrun={payrun}
        claims={payrunClaims}
        viewChangeLog
      />

      <SkipPayrunPrompt
        dialogProps={skipPrompt.dialogProps}
        promptProps={skipPrompt.promptProps}
        onConfirm={handleSkipPayrun}
        onClose={skipPrompt.closeDialog}
      />

      {milestonePayrun?.result && (
        <EditPayrunMilestones
          payrunId={payrun?.id}
          renderButton={(onClick) => {
            openEditMilestones.current = onClick;
            return null;
          }}
        />
      )}

      <Modal
        closable
        width="480px"
        onCancel={handleStatusModalClose}
        title="Update Pay Run Status"
        visible={viewStatusModal}
        footer={[
          <AntdButton
            key="cancel-update-status"
            className="rounded-lg"
            type="ghost"
            onClick={handleStatusModalClose}
          >
            Cancel
          </AntdButton>,
          <AntdButton
            key="save-update-status"
            loading={statusLoading}
            className="ml-2 rounded-lg"
            type="primary"
            onClick={handleSaveStatus}
          >
            Save
          </AntdButton>,
        ]}
      >
        <UpdatePayrunStatus
          payrunStatusId={payrun.payrunStatusId}
          onStatusChange={setSelectedStatusId}
        />
      </Modal>
      <AssignPayrollManagerModal
        ref={assignPayrunManagerModalRef}
        payrun={payrun}
      />
    </>
  );
};

export default PayrunViewOptions;
